// src/features/auth/services/authService.ts

import { LoginCredentials, SignUpData, AuthResponse, ForgotPassword, ResetPasswordData } from '../types';
import { api } from '../../../services/api/axios';

export const authService = {
  // Login API (JWT Auth)
  login: async (credentials: LoginCredentials): Promise<AuthResponse> => {
    let response;
    try {
      response = await api.post('token', {
        username: credentials.email,
        password: credentials.password,
        cfToken: credentials.cfToken,
      });
    } catch (err: any) {
      const apiMessage = err.response?.data?.message || err.response?.data?.error;
      throw new Error(apiMessage || err.message);
    }

    const data = response.data;
    const token = data.token;
    localStorage.setItem('token', token);
    localStorage.setItem('email', data.user_email || credentials.email);

    return {
      success: true,
      user: {
        id: data.user_nicename || '',
        email: data.user_email || credentials.email,
        name: data.user_display_name || '',
        token: token,
      },
    };
  },

  // Sign Up API (WooCommerce)
  signUp: async (data: SignUpData): Promise<AuthResponse> => {
    const addressInfo = {
      first_name: data.firstName,
      last_name: data.lastName,
      address_1: data.address,
      city: data.province,
      postcode: data.postcode,
      country: data.country,
      email: data.email,
      phone: data.phoneNumber,
      phone_code: data.phoneCode,
    };

    let response;
    try {
      response = await api.post('customers', {
        cfToken: data.cfToken,
        email: data.email,
        username: data.email,
        password: data.password,
        first_name: data.firstName,
        last_name: data.lastName,
        billing: addressInfo,
        shipping: addressInfo,
      });
    } catch (err: any) {
      const apiMessage = err.response?.data?.message || err.response?.data?.error;
      throw new Error(apiMessage || err.message);
    }

    const user = response.data;

    return {
      success: true,
      user: {
        id: String(user.id || ''),
        email: user.email || data.email,
        name: `${user.first_name || data.firstName} ${user.last_name || data.lastName}`,
        token: '',
      },
      message: 'Registration Complete',
    };
  },

  // Logout
  logout: () => {
    localStorage.removeItem('token');
  },

  // Check authentication status
  isAuthenticated: (): boolean => {
    return !!localStorage.getItem('token');
  },

  // Get current token
  getToken: (): string | null => {
    return localStorage.getItem('token');
  },

  forgotPassword: async (data: ForgotPassword): Promise<AuthResponse> => {
    try {
      await api.post('/forgot-password', { email: data.email });
    } catch (err: any) {
      const apiMessage = err.response?.data?.message || err.response?.data?.error;
      throw new Error(apiMessage || err.message);
    }
    return {
      success: true,
      message: 'Password reset email has been sent.',
    };
  },

  resetPassword: async (data: ResetPasswordData): Promise<AuthResponse> => {
    try {
      await api.post('/reset-password', {
        key: data.key,
        login: data.login,
        password: data.password,
      });
    } catch (err: any) {
      const apiMessage = err.response?.data?.message || err.response?.data?.error;
      throw new Error(apiMessage || err.message);
    }
    return {
      success: true,
      message: 'Password has been reset successfully.',
    };
  },

  // Check Email — Register Step 1
  checkEmail: async (email: string): Promise<{ success: boolean; available: boolean; message: string }> => {
    try {
      const response = await api.post('/check-email', { email });
      return response.data;
    } catch (err: any) {
      const apiMessage = err.response?.data?.message || err.response?.data?.error;
      throw new Error(apiMessage || err.message);
    }
  },
};
